// ProgramColorTile — shared full-bleed color-tile treatment used by Mode 1
// home browse, Mode 2 /app/browse, and (in a passive variant) the program
// detail page hero. Spec: VOYA9_Frontend_Brand_Correction_Program_Cards.md
//
// Color scheme is sourced from `program.colors` if present (backend), with
// slug-keyed fallbacks in brand.js. Layout: italic serif numbered overlay
// top-right, category eyebrow, bold uppercase name, italic serif tagline,
// state/price footer above a tinted divider.
import { Link } from "react-router-dom";
import { colorSchemeForProgram, categoryNumberForProgram } from "@/lib/brand";
import { fmtMoney } from "@/lib/format";

export default function ProgramColorTile({
  program,
  variant = "explore",      // "explore" | "active" | "available_add" | "coming_soon" | "in_development"
  activeTierName,           // shown when variant === "active"
  to,                       // Link target; defaults to /app/programs/:slug
  featured = false,         // span both columns when true
  testIdPrefix = "program-tile",
}) {
  const scheme = colorSchemeForProgram(program);
  const num = categoryNumberForProgram(program);
  const slug = program?.slug || "";

  const comingSoon = variant === "coming_soon";
  const inDevelopment = variant === "in_development";
  const isActive = variant === "active";
  const availableAdd = variant === "available_add";

  // in_development tiles route to the dedicated coming-soon detail page
  // (waitlist + protocol-plan content) by default.
  const href = to || (inDevelopment
    ? `/app/programs/${slug}/coming-soon`
    : `/app/programs/${slug}`);

  const cta = inDevelopment
    ? "LEARN MORE →"
    : comingSoon
      ? "NOTIFY ME →"
      : isActive
        ? "VIEW →"
        : availableAdd
          ? "ADD →"
          : "EXPLORE →";

  const tagline = program?.tagline || program?.shortDescription || "";
  const priceFromCents = program?.priceFromCents ?? null;
  const priceFromDollars = program?.priceFrom ?? null;
  const priceLabel = priceFromCents != null
    ? `From ${fmtMoney(priceFromCents / 100)}/mo`
    : (priceFromDollars != null ? `From ${fmtMoney(priceFromDollars)}/mo` : "");

  const Body = (
    <div
      data-testid={`${testIdPrefix}-${slug}`}
      data-variant={variant}
      style={{
        position: "relative",
        background: scheme.background,
        padding: "32px 28px",
        minHeight: 260,
        display: "flex",
        flexDirection: "column",
        textDecoration: "none",
        color: scheme.textPrimary,
        opacity: comingSoon ? 0.7 : (inDevelopment ? 0.92 : 1),
        cursor: comingSoon ? "default" : "pointer",
        gridColumn: featured ? "1 / -1" : undefined,
        transition: "transform 0.15s ease",
        // Numeral watermark contained to this card's bounds.
        overflow: "hidden",
      }}
      className={comingSoon ? "" : "program-tile-hover"}
    >
      {/* Italic serif numbered overlay — sized to read as a confident
          editorial flourish, not a faint watermark. Tiles use the older,
          more visible treatment per design direction. paddingRight is
          an ink buffer for italic Instrument Serif's rightward curl so
          overflow: hidden on the card doesn't clip the glyph on mobile. */}
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: 24,
          right: 20,
          zIndex: 1,
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: "clamp(48px, 7vw, 78px)",
          lineHeight: 1,
          opacity: 0.5,
          color: scheme.numberOverlay,
          pointerEvents: "none",
          letterSpacing: "-0.04em",
          paddingRight: 8,
        }}
      >
        {num}
      </span>

      {/* Content stacking context — sits ABOVE the watermark numeral.
          Flex preserved so footer's marginTop: auto still pins it to the
          tile bottom. */}
      <div style={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", flex: 1 }}>

      {/* Category eyebrow + state badge row */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.18em",
            color: scheme.textSecondary,
            textTransform: "uppercase",
          }}
        >
          CATEGORY {num}
        </span>
        {isActive && (
          <span
            data-testid={`${testIdPrefix}-${slug}-active-badge`}
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.16em",
              padding: "3px 9px",
              background: scheme.textPrimary,
              color: scheme.background,
              textTransform: "uppercase",
            }}
          >
            ✓ CURRENTLY ACTIVE
          </span>
        )}
        {comingSoon && (
          <span
            data-testid={`${testIdPrefix}-${slug}-coming-badge`}
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.16em",
              padding: "3px 9px",
              background: scheme.textPrimary,
              color: scheme.background,
              textTransform: "uppercase",
            }}
          >
            COMING SOON
          </span>
        )}
        {inDevelopment && (
          <span
            data-testid={`${testIdPrefix}-${slug}-indev-badge`}
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.16em",
              padding: "3px 9px",
              background: scheme.textPrimary,
              color: scheme.background,
              textTransform: "uppercase",
            }}
          >
            ★ IN DEVELOPMENT
          </span>
        )}
      </div>

      {/* Program name — bold sans, uppercase */}
      <h3
        style={{
          fontFamily: '"DM Sans", -apple-system, sans-serif',
          fontSize: 26,
          fontWeight: 800,
          lineHeight: 1.05,
          letterSpacing: "-0.02em",
          color: scheme.textPrimary,
          margin: "0 0 18px 0",
          textTransform: "uppercase",
          maxWidth: "82%",
        }}
      >
        {(program?.name || "").toUpperCase()}
      </h3>

      {/* Tagline — italic serif */}
      {tagline && (
        <p
          style={{
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontStyle: "italic",
            fontSize: 17,
            color: scheme.textSecondary,
            lineHeight: 1.4,
            margin: 0,
          }}
        >
          "{tagline}"
        </p>
      )}

      {/* Footer pinned to bottom — divider + state/price + CTA */}
      <div
        style={{
          marginTop: "auto",
          paddingTop: 14,
          borderTop: `1px solid ${scheme.borderColor}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: scheme.textPrimary,
            letterSpacing: "0.01em",
          }}
        >
          {isActive
            ? activeTierName || "Active"
            : inDevelopment
              ? "Building now"
              : availableAdd && priceLabel
                ? <>{priceLabel} <span style={{ color: scheme.textSecondary, fontWeight: 500, marginLeft: 6, fontSize: 12 }}>+ $19 review</span></>
                : priceLabel || ""}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.18em",
            color: scheme.textSecondary,
          }}
        >
          {cta}
        </span>
      </div>
      </div>
    </div>
  );

  if (comingSoon) {
    return (
      <div
        style={{ gridColumn: featured ? "1 / -1" : undefined, textDecoration: "none" }}
      >
        {Body}
      </div>
    );
  }
  return (
    <Link
      to={href}
      style={{ textDecoration: "none", gridColumn: featured ? "1 / -1" : undefined }}
    >
      {Body}
    </Link>
  );
}

export function ProgramTilesGrid({ children, gap = 0 }) {
  return (
    <div
      data-testid="program-tiles-grid"
      style={{ display: "grid", gridTemplateColumns: "1fr", gap }}
      className="md:!grid-cols-2"
    >
      {children}
    </div>
  );
}
